package org.teddapp.project.db

import org.teddapp.project.ui.TeddDates

/**
 * Récurrence d'une micro-tâche. « Écouter 10 min d'anglais » revient tous les jours :
 * la tâche n'est pas dupliquée, elle se repose. Quand on la coche, son échéance avance
 * à la prochaine occurrence et elle redevient à faire.
 */
enum class TaskRecurrence(val key: String, val label: String) {
    None("none", "Une seule fois"),
    Daily("daily", "Chaque jour"),
    Weekdays("weekdays", "En semaine"),
    Weekly("weekly", "Chaque semaine"),
    Monthly("monthly", "Chaque mois");

    companion object {
        fun of(raw: String?): TaskRecurrence =
            entries.firstOrNull { it.key == raw } ?: None
    }
}

/**
 * Prochaine occurrence strictement après [fromIso]. Renvoie null si la tâche ne se répète pas.
 * Pour « en semaine », les samedis et dimanches sont sautés.
 */
fun nextOccurrence(recurrence: TaskRecurrence, fromIso: String): String? {
    val days = TeddDates.daysFromIso(fromIso) ?: return null
    return when (recurrence) {
        TaskRecurrence.None -> null
        TaskRecurrence.Daily -> TeddDates.isoFromDays(days + 1)
        TaskRecurrence.Weekdays -> {
            var d = days + 1
            // 5 = samedi, 6 = dimanche dans la convention lundi = 0.
            while (TeddDates.weekdayMondayZero(d) >= 5) d += 1
            TeddDates.isoFromDays(d)
        }
        TaskRecurrence.Weekly -> TeddDates.isoFromDays(days + 7)
        TaskRecurrence.Monthly -> {
            val y = fromIso.substring(0, 4).toInt()
            val m = fromIso.substring(5, 7).toInt()
            val d = fromIso.substring(8, 10).toInt()
            val ny = if (m == 12) y + 1 else y
            val nm = if (m == 12) 1 else m + 1
            val nd = minOf(d, TeddDates.daysInMonth(ny, nm))
            "${ny.toString().padStart(4, '0')}-${nm.toString().padStart(2, '0')}-${nd.toString().padStart(2, '0')}"
        }
    }
}

/**
 * Fait avancer une échéance jusqu'à la première occurrence à partir d'aujourd'hui.
 * Utilisé au démarrage : une tâche quotidienne oubliée hier réapparaît aujourd'hui,
 * pas avec douze jours de retard.
 */
fun catchUpOccurrence(recurrence: TaskRecurrence, dueIso: String?, todayIso: String = TODAY_ISO): String? {
    if (recurrence == TaskRecurrence.None) return null
    val today = TeddDates.daysFromIso(todayIso) ?: return null
    var current = dueIso?.takeIf { TeddDates.isValidIso(it) } ?: return todayIso
    var guard = 0
    while ((TeddDates.daysFromIso(current) ?: today) < today && guard < 800) {
        current = nextOccurrence(recurrence, current) ?: return current
        guard += 1
    }
    return current
}
